import { Link } from '@tanstack/react-router';
import { useRef, useState, type ReactNode } from 'react';
import { haptic } from '../../lib/native';
import { samplePortraitUrl } from '../canvas/samplePortraits';
import { LiveTree, Mark } from '../landing/Landing';
import s from './Onboarding.module.css';
import { useT } from '../../i18n';

const SEEN_KEY = 'lineage.onboarded';

/** Whether this device has been through the intro slides (app only). Storage can be unavailable; then show them. */
export function onboardingSeen() {
  try {
    return localStorage.getItem(SEEN_KEY) === '1';
  } catch {
    return false;
  }
}

function markSeen() {
  try {
    localStorage.setItem(SEEN_KEY, '1');
  } catch {
    /* shown again next time; harmless */
  }
}

const FACES = ['thomas', 'helen', 'lucy', 'daniel', 'priya', 'leo'] as const;

function Faces() {
  return (
    <div className={s.faces} aria-hidden="true">
      {FACES.map((k, i) => (
        <span key={k} className={s.face} style={{ background: `var(--p${i})` }}>
          <img src={samplePortraitUrl(k)} alt="" draggable={false} onError={(e) => e.currentTarget.remove()} />
        </span>
      ))}
    </div>
  );
}

function Invite() {
  const [ask, answer, reply] = useT().onboarding.chat;
  return (
    <div className={s.chat} aria-hidden="true">
      <div className={`${s.bubble} ${s.them}`}>{ask}</div>
      <div className={`${s.bubble} ${s.mine}`}>
        {answer}
        <span className={s.link}>mylineage.app/i/…</span>
      </div>
      <div className={`${s.bubble} ${s.them}`}>{reply}</div>
    </div>
  );
}

function Lock() {
  const points = useT().onboarding.points;
  return (
    <div className={s.lock} aria-hidden="true">
      <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
        <rect x="5" y="11" width="14" height="9" rx="2.5" />
        <path d="M8 11V8a4 4 0 0 1 8 0v3" />
      </svg>
      <ul className={s.points}>
        {points.map((p) => <li key={p}>{p}</li>)}
      </ul>
    </div>
  );
}

/** Pictures for the slides, in order; the words come from the screen language. */
const ART: ReactNode[] = [<Faces key="faces" />, <LiveTree key="tree" compact />, <Invite key="invite" />, <Lock key="lock" />];

/** App intro: swipe or tap Next through a few slides, then sign in. Shown once per device. */
export function Onboarding({ onDone }: { onDone: () => void }) {
  const t = useT().onboarding;
  const SLIDES = t.slides.map((sl, i) => ({ ...sl, art: ART[i] }));
  const track = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);
  const last = index === SLIDES.length - 1;

  const go = (i: number) => {
    const el = track.current;
    if (!el) return;
    el.scrollTo({ left: i * el.clientWidth, behavior: 'smooth' });
  };
  const finish = () => {
    markSeen();
    onDone();
  };

  return (
    <div className={s.root}>
      <header className={s.top}>
        <span className={s.brand}>
          <Mark size={20} /> Lineage
        </span>
        {!last && (
          <button type="button" className="btn btn-ghost btn-sm" onClick={finish}>
            {t.skip}
          </button>
        )}
      </header>
      <div
        ref={track}
        className={s.track}
        onScroll={(e) => {
          const el = e.currentTarget;
          const i = Math.round(el.scrollLeft / el.clientWidth);
          if (i !== index) {
            setIndex(i);
            haptic();
          }
        }}
        aria-roledescription="carousel"
      >
        {SLIDES.map((sl, i) => (
          <section key={i} className={s.slide} aria-roledescription="slide" aria-label={t.slideOf(i + 1, SLIDES.length)} aria-hidden={i !== index}>
            <div className={s.art}>{sl.art}</div>
            <h1 className={s.title}>{sl.title}</h1>
            <p className={s.body}>{sl.body}</p>
          </section>
        ))}
      </div>
      <footer className={s.bottom}>
        <div className={s.dots} role="tablist" aria-label={t.slides_}>
          {SLIDES.map((sl, i) => (
            <button key={i} type="button" role="tab" aria-selected={i === index} aria-label={t.slide(i + 1)} className={s.dot} onClick={() => go(i)} />
          ))}
        </div>
        {last ? (
          <div className="stack" style={{ gap: 10, width: '100%' }}>
            <button type="button" className="btn btn-primary" style={{ minHeight: 52 }} onClick={finish}>
              {t.getStarted}
            </button>
            <Link to="/demo" className="btn" style={{ minHeight: 52 }} onClick={markSeen}>
              {t.explore}
            </Link>
          </div>
        ) : (
          <button type="button" className="btn btn-primary" style={{ minHeight: 52, width: '100%' }} onClick={() => go(index + 1)}>
            {t.next}
          </button>
        )}
      </footer>
    </div>
  );
}
